/**
 * Concept images on the concept-test results page (spec 83).
 *
 * The containment contract: the FRAME sets the size and clips; the image is drawn `object-contain` inside it,
 * so no image of any size or aspect can push the layout. A slot that cannot keep its minimum size on a narrow
 * screen is hidden (`hideBelow`), never squeezed.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImageOff, X } from 'lucide-react';
import { resolveImageSrc } from '../../../../utils/resolveImageSrc';

type HideBelow = 'sm';
type ThumbSize = 'sm' | 'md';
type FrameSize = 'winner' | 'coverWinner' | 'cover' | 'summary' | 'modal';

const HIDE_BELOW: Record<HideBelow, string> = { sm: 'hidden sm:flex' };
const THUMB_SIZE: Record<ThumbSize, string> = { sm: 'w-10 h-10', md: 'w-12 h-12' };
const FRAME_SIZE: Record<FrameSize, string> = {
  winner: 'w-32 h-40',
  coverWinner: 'w-40 h-40',
  // Full width under the title on phones; beside it from `sm`.
  cover: 'w-full h-48 sm:w-48 lg:w-56 lg:h-56',
  summary: 'w-32 h-32',
  modal: 'w-full h-56 sm:h-64',
};
const DARK_FRAME_STYLE: React.CSSProperties = {
  borderColor: 'rgba(7, 229, 209, 0.25)',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
};
const CONTAINED_IMAGE = 'max-w-full max-h-full object-contain';

const layoutClass = (hideBelow?: HideBelow) => (hideBelow ? HIDE_BELOW[hideBelow] : 'flex');

const ConceptLightbox: React.FC<{ src: string; alt: string; onClose: () => void }> = ({
  src,
  alt,
  onClose,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close enlarged image"
        className="absolute top-3 right-3 rounded-lg p-2 text-white/80 hover:text-white hover:bg-white/10"
      >
        <X className="h-5 w-5" />
      </button>
      <div
        data-testid="concept-lightbox-frame"
        className="flex max-h-[80vh] max-w-[90vw] items-center justify-center overflow-hidden rounded-lg bg-white p-2"
        onClick={(e) => e.stopPropagation()}
      >
        <img src={src} alt={alt} className="max-h-[80vh] max-w-[90vw] object-contain" />
      </div>
    </div>,
    document.body,
  );
};

/** Small square thumbnail beside a concept; a neutral tile when the concept has no (loadable) image. */
export const ConceptThumb: React.FC<{
  src: string | null | undefined;
  alt: string;
  size: ThumbSize;
  hideBelow?: HideBelow;
}> = ({ src, alt, size, hideBelow }) => {
  const [failed, setFailed] = useState(false);
  const [enlarged, setEnlarged] = useState(false);
  const resolved = resolveImageSrc(src);
  const frame = `${THUMB_SIZE[size]} ${layoutClass(hideBelow)} flex-shrink-0 overflow-hidden rounded-lg items-center justify-center`;

  if (!resolved || failed) {
    return (
      <div
        data-testid="concept-thumb"
        role="img"
        aria-label={`${alt}: no image`}
        className={`${frame} border border-dashed border-gray-300 bg-gray-50 text-gray-400`}
      >
        <ImageOff className="h-4 w-4" />
      </div>
    );
  }
  return (
    <>
      <button
        type="button"
        data-testid="concept-thumb"
        aria-label={`Enlarge ${alt}`}
        onClick={() => setEnlarged(true)}
        className={`${frame} cursor-zoom-in border border-gray-200 bg-white p-0.5 hover:border-teal-300`}
      >
        <img
          src={resolved}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={CONTAINED_IMAGE}
          onError={() => setFailed(true)}
        />
      </button>
      {enlarged && <ConceptLightbox src={resolved} alt={alt} onClose={() => setEnlarged(false)} />}
    </>
  );
};

/** Larger framed image (covers, winner card, details popup); renders nothing when there is no image. */
export const ConceptImageFrame: React.FC<{
  src: string | null | undefined;
  alt: string;
  size: FrameSize;
  hideBelow?: HideBelow;
}> = ({ src, alt, size, hideBelow }) => {
  const [failed, setFailed] = useState(false);
  const [enlarged, setEnlarged] = useState(false);
  const resolved = resolveImageSrc(src);
  if (!resolved || failed) return null;

  return (
    <>
      <button
        type="button"
        data-testid="concept-image-frame"
        aria-label={`Enlarge ${alt}`}
        onClick={() => setEnlarged(true)}
        style={DARK_FRAME_STYLE}
        className={`${FRAME_SIZE[size]} ${layoutClass(hideBelow)} flex-shrink-0 cursor-zoom-in items-center justify-center overflow-hidden rounded-lg border-2 p-2`}
      >
        <img
          src={resolved}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={`${CONTAINED_IMAGE} rounded`}
          onError={() => setFailed(true)}
        />
      </button>
      {enlarged && <ConceptLightbox src={resolved} alt={alt} onClose={() => setEnlarged(false)} />}
    </>
  );
};
